import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { firstValueFrom } from 'rxjs';
import {
  isHeicDocumentExtension,
  isPreviewableDocumentImage,
  resolveDocumentImageExtension,
} from 'src/app/core/helpers/document-image-preview.util';
import { PatientDocsService } from 'src/app/core/services/patient-docs.service';
import Swal from 'sweetalert2';

@Component({
  selector: 'app-document-preview',
  templateUrl: './document-preview.component.html',
  styleUrls: ['./document-preview.component.scss']
})
export class DocumentPreviewComponent implements OnInit, OnDestroy {
  @Input() signedLink: string;
  /** Original document name (may include extension when the URL does not). */
  @Input() documentName?: string;
  /** Original file extension from API metadata. */
  @Input() documentExtension?: string;
  /** File key used to fetch the blob through the API when needed. */
  @Input() fileKey?: string;
  /** When true, hide modal chrome and errors do not close a parent modal (used inside another modal). */
  @Input() embedded = false;
  isLoading: boolean;
  isImage: boolean = false;

  /** Display width as % of the preview container (default 50%). */
  imageZoomPercent = 50;
  readonly zoomStep = 10;
  readonly minZoomPercent = 10;
  readonly maxZoomPercent = 300;
  readonly defaultZoomPercent = 50;

  private previewBlobUrl: string | null = null;

  constructor(
    private readonly _docService: PatientDocsService,
    public bsModalRef: BsModalRef
  ) {
  }

  ngOnInit(): void {
    void this.initializePreview();
  }

  ngOnDestroy(): void {
    this.revokePreviewBlob();
  }

  private async initializePreview(): Promise<void> {
    if (!this.signedLink) {
      if (!this.embedded) {
        this.showErrorMessage();
      }
      return;
    }

    if (this.signedLink.startsWith('blob:')) {
      this.applyImageDetection(this.signedLink);
      return;
    }

    if (this.signedLink.startsWith('https://') || this.signedLink.startsWith('http://')) {
      await this.preparePreviewLink(this.signedLink);
      return;
    }

    this.isLoading = true;
    try {
      const link = await firstValueFrom(
        this._docService.getDocumentLinkByFileName(this.signedLink)
      );
      await this.preparePreviewLink(link);
    } catch {
      this.showErrorMessage();
    } finally {
      this.isLoading = false;
    }
  }

  private async preparePreviewLink(link: string): Promise<void> {
    this.applyImageDetection(link);
    if (!this.isImage) {
      this.signedLink = link;
      return;
    }

    const extension = resolveDocumentImageExtension(
      this.documentExtension,
      this.documentName,
      null,
      link
    );
    if (!isHeicDocumentExtension(extension)) {
      this.signedLink = link;
      return;
    }

    this.isLoading = true;
    try {
      const sourceBlob = await this.fetchPreviewBlob(link);
      const heic2any = (await import('heic2any')).default;
      const converted = await heic2any({
        blob: sourceBlob,
        toType: 'image/jpeg',
        quality: 0.9,
      });
      const jpegBlob = Array.isArray(converted) ? converted[0] : converted;
      this.revokePreviewBlob();
      this.previewBlobUrl = URL.createObjectURL(jpegBlob);
      this.signedLink = this.previewBlobUrl;
      this.imageZoomPercent = this.defaultZoomPercent;
    } catch {
      this.showErrorMessage();
    } finally {
      this.isLoading = false;
    }
  }

  private applyImageDetection(url: string): void {
    this.isImage = isPreviewableDocumentImage(
      this.documentExtension,
      this.documentName,
      null,
      url
    );
    if (this.isImage) {
      this.imageZoomPercent = this.defaultZoomPercent;
    }
  }

  private async fetchPreviewBlob(link: string): Promise<Blob> {
    const key = (this.fileKey || link).trim();
    if (key) {
      try {
        return await firstValueFrom(this._docService.getDocumentFileByKey(key));
      } catch {
        // Fall back to direct fetch for presigned URLs.
      }
    }

    const response = await fetch(link);
    if (!response.ok) {
      throw new Error('Failed to fetch document for preview');
    }
    return response.blob();
  }

  private revokePreviewBlob(): void {
    if (this.previewBlobUrl) {
      URL.revokeObjectURL(this.previewBlobUrl);
      this.previewBlobUrl = null;
    }
  }

  zoomIn(): void {
    this.imageZoomPercent = Math.min(
      this.maxZoomPercent,
      this.imageZoomPercent + this.zoomStep
    );
  }

  zoomOut(): void {
    this.imageZoomPercent = Math.max(
      this.minZoomPercent,
      this.imageZoomPercent - this.zoomStep
    );
  }

  resetImageZoom(): void {
    this.imageZoomPercent = this.defaultZoomPercent;
  }

  showErrorMessage() {
    if (this.embedded) {
      return;
    }
    this.bsModalRef.hide();
    Swal.fire({
      title: 'Invalid Url',
      text: "Could not preview document because the link does not appear to be valid.",
      icon: 'warning',
      showCancelButton: false,
      confirmButtonColor: '#513DCA',
      confirmButtonText: 'OK'
    });
  }
}
